import React, { useMemo, useState } from "react";
import { useQuery } from "react-query";

import { apiGetList } from "services/api";
import { Accordion } from "components/Accordion";
import { Checkbox } from "components/Checkbox";
import { Input } from "components/Input";
import { Select } from "components/Select";
import { MediumText, RegularText } from "components/Text";
import { VerticalSpace, Divider } from "components/Spacing";

import styles from "./styles.module.scss";

const LABEL = 210;
const INPUT_W = 260;
const MANAGED_KEYS = new Set([
  "newInterface",
  "askItemCount",
  "bankTerminalEnabled",
  "bankAcquiringType",
  "bankTerminalIp",
  "bankTerminalPort",
  "bankTerminalEcr",
  "onlineCashRegisterEnabled",
  "onlineCashRegisterType",
  "onlineCashRegisterIp",
  "onlineCashRegisterPort",
  "defaultPrintTemplateSlipId",
  "defaultPrintTemplateNonfiscalId",
  "defaultPrintTemplateInvoiceId",
]);

const getTemplateKind = (template) => {
  if (Number(template.slipdefault) === 1) return "slip";
  if (Number(template.nakladnayadefault) === 1) return "nakladnaya";
  return "other";
};

const parseSettingsObject = (raw) => {
  if (!raw?.trim()) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch {
    return null;
  }
};

const buildSettingsString = (form, extraRaw) => {
  let merged = { ...form };

  if (extraRaw?.trim()) {
    try {
      const extra = JSON.parse(extraRaw);
      if (extra && typeof extra === "object" && !Array.isArray(extra)) {
        merged = { ...extra, ...form };
      }
    } catch {
      // Пока доп. JSON невалиден — сохраняем только поля формы
    }
  }

  Object.keys(merged).forEach((key) => {
    const val = merged[key];
    if (val === "" || val === undefined) delete merged[key];
  });

  return Object.keys(merged).length ? JSON.stringify(merged, null, 2) : "";
};

const emptyForm = () => ({
  newInterface: false,
  askItemCount: false,
  bankTerminalEnabled: false,
  bankAcquiringType: "sber",
  bankTerminalIp: "",
  bankTerminalPort: "",
  bankTerminalEcr: "",
  onlineCashRegisterEnabled: false,
  onlineCashRegisterType: "atol_27f",
  onlineCashRegisterIp: "",
  onlineCashRegisterPort: "",
  defaultPrintTemplateSlipId: "",
  defaultPrintTemplateNonfiscalId: "",
  defaultPrintTemplateInvoiceId: "",
});

const formFromObject = (obj) => {
  const base = emptyForm();
  if (!obj) return base;

  return {
    ...base,
    newInterface: Boolean(obj.newInterface),
    askItemCount: Boolean(obj.askItemCount),
    bankTerminalEnabled: Boolean(obj.bankTerminalEnabled),
    bankAcquiringType: obj.bankAcquiringType ?? base.bankAcquiringType,
    bankTerminalIp: obj.bankTerminalIp ?? "",
    bankTerminalPort: obj.bankTerminalPort ?? "",
    bankTerminalEcr: obj.bankTerminalEcr ?? "",
    onlineCashRegisterEnabled: Boolean(obj.onlineCashRegisterEnabled),
    onlineCashRegisterType: obj.onlineCashRegisterType ?? base.onlineCashRegisterType,
    onlineCashRegisterIp: obj.onlineCashRegisterIp ?? "",
    onlineCashRegisterPort: obj.onlineCashRegisterPort ?? "",
    defaultPrintTemplateSlipId:
      obj.defaultPrintTemplateSlipId != null
        ? String(obj.defaultPrintTemplateSlipId)
        : "",
    defaultPrintTemplateNonfiscalId:
      obj.defaultPrintTemplateNonfiscalId != null
        ? String(obj.defaultPrintTemplateNonfiscalId)
        : "",
    defaultPrintTemplateInvoiceId:
      obj.defaultPrintTemplateInvoiceId != null
        ? String(obj.defaultPrintTemplateInvoiceId)
        : "",
  };
};

const objectFromForm = (form) => ({
  newInterface: form.newInterface,
  askItemCount: form.askItemCount,
  bankTerminalEnabled: form.bankTerminalEnabled,
  bankAcquiringType: form.bankAcquiringType || "sber",
  bankTerminalIp: form.bankTerminalIp || "",
  bankTerminalPort: form.bankTerminalPort || "",
  bankTerminalEcr: form.bankTerminalEcr || "",
  onlineCashRegisterEnabled: form.onlineCashRegisterEnabled,
  onlineCashRegisterType: form.onlineCashRegisterType || "atol_27f",
  onlineCashRegisterIp: form.onlineCashRegisterIp || "",
  onlineCashRegisterPort: form.onlineCashRegisterPort || "",
  defaultPrintTemplateSlipId: form.defaultPrintTemplateSlipId
    ? Number(form.defaultPrintTemplateSlipId)
    : null,
  defaultPrintTemplateNonfiscalId: form.defaultPrintTemplateNonfiscalId
    ? Number(form.defaultPrintTemplateNonfiscalId)
    : null,
  defaultPrintTemplateInvoiceId: form.defaultPrintTemplateInvoiceId
    ? Number(form.defaultPrintTemplateInvoiceId)
    : null,
});

/** Редактор настроек станции в сворачиваемом блоке */
export const StationSettingsEditor = ({ value, onChange }) => {
  const [collapsed, setCollapsed] = useState(true);
  const [form, setForm] = useState(() => {
    const parsed = parseSettingsObject(value);
    if (parsed === null) return emptyForm();

    const managed = {};
    Object.entries(parsed).forEach(([key, val]) => {
      if (MANAGED_KEYS.has(key)) managed[key] = val;
    });
    return formFromObject(managed);
  });
  const [extraJson, setExtraJson] = useState(() => {
    const parsed = parseSettingsObject(value);
    if (parsed === null) return "";

    const extra = {};
    Object.entries(parsed).forEach(([key, val]) => {
      if (!MANAGED_KEYS.has(key)) extra[key] = val;
    });
    return Object.keys(extra).length ? JSON.stringify(extra, null, 2) : "";
  });
  const [showAdvanced, setShowAdvanced] = useState(false);

  const templatesQuery = useQuery(["cheque-print-templates"], () =>
    apiGetList("print-templates", { page: 1, pageSize: 1000 })
  );

  const templates = templatesQuery.data?.items ?? [];

  const templateOptionsByKind = useMemo(() => {
    const empty = [{ value: "", label: "— не выбран —" }];
    const toOption = (t) => ({
      value: String(t.printtemplateid),
      label: `${t.name} (#${t.printtemplateid})`,
    });

    return {
      slip: empty.concat(
        templates.filter((t) => getTemplateKind(t) === "slip").map(toOption)
      ),
      other: empty.concat(
        templates.filter((t) => getTemplateKind(t) === "other").map(toOption)
      ),
      invoice: empty.concat(
        templates.filter((t) => getTemplateKind(t) === "nakladnaya").map(toOption)
      ),
    };
  }, [templates]);

  const updateForm = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      onChange(buildSettingsString(objectFromForm(next), extraJson));
      return next;
    });
  };

  const updateExtraJson = (nextExtra) => {
    setExtraJson(nextExtra);
    onChange(buildSettingsString(objectFromForm(form), nextExtra));
  };

  const selectValue = (options, current) =>
    options.find((o) => String(o.value) === String(current)) || options[0];

  return (
    <Accordion
      withIcon
      filled
      collapsed={collapsed}
      handleCollapse={() => setCollapsed((v) => !v)}
      parent={<MediumText size={16}>Настройки приложения</MediumText>}
      childrenClassName={styles.settingsAccordionBody}
    >
      <RegularText size={12} color="muted">
        Заполнение необязательно. Если блок пустой, приложение станции использует
        значения по умолчанию.
      </RegularText>
      <VerticalSpace size={14} />

      <MediumText size={12}>UI</MediumText>
      <VerticalSpace size={10} />
      <div className={styles.settingsChecks}>
        <Checkbox
          checked={form.newInterface}
          onChange={(checked) => updateForm({ newInterface: checked })}
          label="Новый интерфейс (newInterface)"
        />
        <Checkbox
          checked={form.askItemCount}
          onChange={(checked) => updateForm({ askItemCount: checked })}
          label="Спрашивать количество при добавлении (askItemCount)"
        />
      </div>

      <VerticalSpace size={16} />
      <Divider />
      <VerticalSpace size={16} />

      <MediumText size={12}>Банковский терминал</MediumText>
      <VerticalSpace size={10} />
      <Checkbox
        checked={form.bankTerminalEnabled}
        onChange={(checked) => updateForm({ bankTerminalEnabled: checked })}
        label="Терминал включён (bankTerminalEnabled)"
      />
      <VerticalSpace size={10} />
      <Select
        label="Тип эквайринга"
        labelWidth={LABEL}
        width={INPUT_W}
        options={[{ value: "sber", label: "sber" }]}
        value={selectValue([{ value: "sber", label: "sber" }], form.bankAcquiringType)}
        onChange={(val) => updateForm({ bankAcquiringType: val?.value ?? "sber" })}
      />
      <VerticalSpace size={10} />
      <Input
        label="IP терминала"
        labelWidth={LABEL}
        inputWidth={INPUT_W}
        value={form.bankTerminalIp}
        onChange={(ev) => updateForm({ bankTerminalIp: ev.target.value })}
        placeholder="192.168.0.10"
      />
      <VerticalSpace size={10} />
      <Input
        label="Порт терминала"
        labelWidth={LABEL}
        inputWidth={INPUT_W}
        value={form.bankTerminalPort}
        onChange={(ev) => updateForm({ bankTerminalPort: ev.target.value })}
        placeholder="5555"
      />
      <VerticalSpace size={10} />
      <Input
        label="ECR id"
        labelWidth={LABEL}
        inputWidth={INPUT_W}
        value={form.bankTerminalEcr}
        onChange={(ev) => updateForm({ bankTerminalEcr: ev.target.value })}
      />

      <VerticalSpace size={16} />
      <Divider />
      <VerticalSpace size={16} />

      <MediumText size={12}>Онлайн-касса</MediumText>
      <VerticalSpace size={10} />
      <Checkbox
        checked={form.onlineCashRegisterEnabled}
        onChange={(checked) =>
          updateForm({ onlineCashRegisterEnabled: checked })
        }
        label="Касса включена (onlineCashRegisterEnabled)"
      />
      <VerticalSpace size={10} />
      <Select
        label="Тип кассы"
        labelWidth={LABEL}
        width={INPUT_W}
        options={[{ value: "atol_27f", label: "atol_27f" }]}
        value={selectValue(
          [{ value: "atol_27f", label: "atol_27f" }],
          form.onlineCashRegisterType
        )}
        onChange={(val) =>
          updateForm({ onlineCashRegisterType: val?.value ?? "atol_27f" })
        }
      />
      <VerticalSpace size={10} />
      <Input
        label="IP кассы"
        labelWidth={LABEL}
        inputWidth={INPUT_W}
        value={form.onlineCashRegisterIp}
        onChange={(ev) => updateForm({ onlineCashRegisterIp: ev.target.value })}
      />
      <VerticalSpace size={10} />
      <Input
        label="Порт кассы"
        labelWidth={LABEL}
        inputWidth={INPUT_W}
        value={form.onlineCashRegisterPort}
        onChange={(ev) => updateForm({ onlineCashRegisterPort: ev.target.value })}
      />

      <VerticalSpace size={16} />
      <Divider />
      <VerticalSpace size={16} />

      <MediumText size={12}>Шаблоны чеков по умолчанию</MediumText>
      <VerticalSpace size={10} />
      <Select
        label="Слип"
        labelWidth={LABEL}
        width={INPUT_W}
        isLoading={templatesQuery.isLoading}
        options={templateOptionsByKind.slip}
        value={selectValue(
          templateOptionsByKind.slip,
          form.defaultPrintTemplateSlipId
        )}
        onChange={(val) =>
          updateForm({ defaultPrintTemplateSlipId: val?.value ?? "" })
        }
      />
      <VerticalSpace size={10} />
      <Select
        label="Нефискальный"
        labelWidth={LABEL}
        width={INPUT_W}
        isLoading={templatesQuery.isLoading}
        options={templateOptionsByKind.other}
        value={selectValue(
          templateOptionsByKind.other,
          form.defaultPrintTemplateNonfiscalId
        )}
        onChange={(val) =>
          updateForm({ defaultPrintTemplateNonfiscalId: val?.value ?? "" })
        }
      />
      <VerticalSpace size={10} />
      <Select
        label="Накладная"
        labelWidth={LABEL}
        width={INPUT_W}
        isLoading={templatesQuery.isLoading}
        options={templateOptionsByKind.invoice}
        value={selectValue(
          templateOptionsByKind.invoice,
          form.defaultPrintTemplateInvoiceId
        )}
        onChange={(val) =>
          updateForm({ defaultPrintTemplateInvoiceId: val?.value ?? "" })
        }
      />

      <VerticalSpace size={16} />
      <button
        type="button"
        className={styles.settingsToggle}
        onClick={() => setShowAdvanced((v) => !v)}
      >
        <MediumText size={12}>
          {showAdvanced ? "▾" : "▸"} Дополнительный JSON
        </MediumText>
      </button>
      {showAdvanced && (
        <>
          <VerticalSpace size={8} />
          <RegularText size={12} color="muted">
            Прочие ключи настроек, не представленные в форме выше.
          </RegularText>
          <VerticalSpace size={8} />
          <textarea
            className={styles.settingsArea}
            value={extraJson}
            onChange={(ev) => updateExtraJson(ev.target.value)}
            placeholder={'{\n  "bankTerminalPaymentIds": [1, 2]\n}'}
            spellCheck={false}
          />
        </>
      )}
    </Accordion>
  );
};
